class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.openTimeoutId = null;
    this.isOpen = false;
    this.addEventListener('keyup', (evt) => {
      if (evt.code !== 'Escape') return;
      if (this.classList.contains('drawer--upsell-qv-open') && typeof window.closeDrawerUpsellQuickView === 'function') {
        window.closeDrawerUpsellQuickView();
        return;
      }
      this.close();
    });
    this.addEventListener('click', (event) => {
      if (event.target.closest('#CartDrawer-Overlay, .drawer__close')) {
        this.close();
      }
    });
    this.setHeaderCartIconAccessibility();
    setUpsell();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    if (this.openTimeoutId) {
      clearTimeout(this.openTimeoutId);
    }

    if (this.classList.contains('is-closing')) {
      if (this.closeFallbackId) {
        clearTimeout(this.closeFallbackId);
        this.closeFallbackId = null;
      }
      this.classList.remove('is-closing');
    }

    this.isOpen = true;

    // Load Graphene mini-cart upsells on open (homepage/other pages never get a PLP category load).
    if (typeof setUpsell === 'function') setUpsell();
    if (typeof syncCartQuantityStockLimits === 'function') syncCartQuantityStockLimits();

    // Timeout helps trigger the slide-in animation; must be cancelled in close() or the backdrop can stick.
    this.openTimeoutId = setTimeout(() => {
      this.openTimeoutId = null;
      if (!this.isOpen) return;

      this.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');

      const drawerSlide = this.getDrawerSlideEl();
      const drawerInner = this.querySelector('.drawer__inner');
      if (!drawerSlide) return;

      window.setTimeout(() => {
        if (this.classList.contains('active')) this.openUpsellPanel();
      }, 520);

      drawerSlide.addEventListener(
        'transitionend',
        (event) => {
          if (event.target !== drawerSlide || event.propertyName !== 'transform') return;
          if (!this.classList.contains('active')) return;

          const containerToTrapFocusOn = document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__close') || drawerInner;
          if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement);
          this.openUpsellPanel();
        },
        { once: true }
      );
    });

    if (typeof window.dispatchViewCart === 'function') {
      fetch('/cart.js').then((res) => res.json()).then((data) => {
        window.dispatchViewCart(data);
      }).catch(function() {});
    }
  }

  close() {
    if (!this.isOpen && !this.classList.contains('is-closing')) return;

    this.isOpen = false;
    if (typeof window.closeDrawerUpsellQuickView === 'function') {
      window.closeDrawerUpsellQuickView();
    }
    this.closeUpsellPanel();

    if (this.openTimeoutId) {
      clearTimeout(this.openTimeoutId);
      this.openTimeoutId = null;
    }

    if (this.classList.contains('is-closing')) return;

    if (!this.classList.contains('active')) {
      this.finishClose();
      return;
    }

    this.classList.add('is-closing');
    this.classList.remove('active');

    const drawerSlide = this.getDrawerSlideEl();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!drawerSlide || reducedMotion) {
      this.finishClose();
      return;
    }

    const onTransitionEnd = (event) => {
      if (event.target !== drawerSlide || event.propertyName !== 'transform') return;
      this.finishClose();
    };

    drawerSlide.addEventListener('transitionend', onTransitionEnd, { once: true });

    if (this.closeFallbackId) clearTimeout(this.closeFallbackId);
    this.closeFallbackId = window.setTimeout(() => {
      if (this.classList.contains('is-closing')) this.finishClose();
    }, 600);
  }

  getDrawerSlideEl() {
    return this.querySelector('.drawer__shell') || this.querySelector('.drawer__inner');
  }

  finishClose() {
    if (this.closeFallbackId) {
      clearTimeout(this.closeFallbackId);
      this.closeFallbackId = null;
    }

    this.closeUpsellPanel();
    if (typeof window.closeDrawerUpsellQuickView === 'function') {
      window.closeDrawerUpsellQuickView();
    }
    this.isOpen = false;
    this.classList.remove('active', 'animate', 'is-closing');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  openUpsellPanel() {
    if (window.matchMedia('(max-width: 900px)').matches) return;
    if (this.classList.contains('is-empty')) return;
    if (this.querySelector('cart-drawer-items.is-empty')) return;

    const panel = this.querySelector('.drawer__upsell-panel');
    const target = this.querySelector('#drawer__upsell');
    if (!panel || !target?.children.length) return;

    panel.hidden = false;
    requestAnimationFrame(() => {
      if (this.classList.contains('active') && !this.classList.contains('is-empty')) {
        this.classList.add('drawer--upsell-open');
      }
    });
  }

  closeUpsellPanel() {
    this.classList.remove('drawer--upsell-open');
    const panel = this.querySelector('.drawer__upsell-panel');
    if (!panel) return;

    const hidePanel = () => {
      if (!this.classList.contains('drawer--upsell-open') && !this.classList.contains('active')) {
        panel.hidden = true;
      }
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      hidePanel();
      return;
    }

    panel.addEventListener('transitionend', hidePanel, { once: true });
    window.setTimeout(hidePanel, 400);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    const data = await getCart();
    updateCartLeadTime(data);
    if (typeof syncCartQuantityStockLimits === 'function') syncCartQuantityStockLimits();
    deliveryPromos(data);
    setUpsell();
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
    this.disposalPopup();
    window.loyaltylion && window.loyaltylion.ui.refresh();
  }
  async renderContentsSwatch(data, removeDrawers, openDrawer) {
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.classList.remove('is-empty');

    // Move upsell out of cart-drawer-items before replace (mobile slot lives inside it).
    const cartItems = this.querySelector('cart-drawer-items');
    if (typeof cartItems?.prepareForSectionRender === 'function') {
      cartItems.prepareForSectionRender();
    }

    try {
      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      // Match CartDrawerItems qty AJAX: replace line items + full footer (not only totals).
      const selectors = ['cart-drawer-items', '.drawer__footer'];
      for (const selector of selectors) {
        const targetElement = document.querySelector(selector);
        const sourceElement = html.querySelector(selector);
        if (targetElement && sourceElement) {
          targetElement.replaceWith(sourceElement);
        }
      }
    } catch (e) {
      console.error(e);
    }

    updateCartLeadTime(data);
    deliveryPromos(data);
    setUpsell();

    if (openDrawer !== true) {
      setTimeout(() => {
        this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
        this.open();
      });
    }

    const cartCount = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
    if (cartCount && data?.item_count != null) {
      cartCount.innerHTML = data.item_count;
    }

    if (removeDrawers) {
      const drawer = getElement('[data-module="drawers"]');
      drawer?.classList.remove('drw-Drawers-active');
      window.syncPdpDrawerScrollLock?.(false);
      this.disposalPopup();
    }

    setTimeout(() => {
      window.loyaltylion && window.loyaltylion.ui.refresh();
    }, 1000);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  disposalPopup(){
    const disposalPopup = getElements('.cart-item__disposal .js-Cart_mattressDisposalInfo');
    if (disposalPopup) {
      const disposalModal = document.body.querySelector('.js-Product_mattressDisposalModal');
      const disposalModalClose = document.body.querySelector('.js-Product_mattressDisposalModalClose');
      if (disposalModal && disposalModalClose) {
        disposalPopup.forEach(el => {
          el.addEventListener('click', () => disposalModal.setAttribute('aria-expanded', 'true'));
        });
        disposalModalClose.addEventListener('click', () => disposalModal.setAttribute('aria-expanded', 'false'));
      }
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CwBadgeRow extends HTMLElement {
  #intervalId = null;

  connectedCallback() {
    this.#init();
  }

  disconnectedCallback() {
    if (this.#intervalId) {
      clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
  }

  #init() {
    const items = [...this.querySelectorAll('.cw-badge-row__text')];

    if (this.#intervalId) {
      clearInterval(this.#intervalId);
      this.#intervalId = null;
    }

    items.forEach((item) => {
      item.classList.remove('is-active');
      item.setAttribute('aria-hidden', 'true');
    });

    if (items.length === 0) {
      return;
    }

    if (items.length === 1 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.classList.add('cw-badge-row--static');
      items.forEach((item) => {
        item.classList.add('is-active');
        item.setAttribute('aria-hidden', 'false');
      });
      return;
    }

    this.classList.remove('cw-badge-row--static');

    let activeIndex = 0;

    const setActive = (index) => {
      items.forEach((item, itemIndex) => {
        const isActive = itemIndex === index;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
    };

    setActive(activeIndex);

    this.#intervalId = setInterval(() => {
      activeIndex = (activeIndex + 1) % items.length;
      setActive(activeIndex);
    }, 4000);
  }
}

customElements.define('cw-badge-row', CwBadgeRow);

class CartDrawerItems extends CartItems {
  prepareForSectionRender() {
    const upsell = getDrawerUpsellRoot();
    const desktopPanel = document.querySelector('.drawer__upsell-panel');
    if (!upsell || !desktopPanel || desktopPanel.contains(upsell)) return;

    desktopPanel.appendChild(upsell);
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: 'cart-drawer-items',
        replaceWith: true,
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__footer',
        replaceWith: true,
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

const deliveryPromos = (data) => {
  const messageArray = []
  const lineItemsWithProductsSets = data.items
  const {
    set1Enabled,
    set1Type1,
    set1Type2,
    set1Message1,
    set1Message2,
    set1QualifiesMessage,
    set1Url1,
    set1Url2,
    set2Enabled,
    set2Type1,
    set2Type2,
    set2Message1,
    set2Message2,
    set2QualifiesMessage,
    set2Url1,
    set2Url2,
    set3Enabled,
    set3Type1,
    set3Type2,
    set3Message1,
    set3Message2,
    set3QualifiesMessage,
    set3Url1,
    set3Url2,
  } = window.theme.pushCartSetMessages

  let discountPrice = 0
  const getSetMessage = (
    item,
    type1,
    type2,
    message1,
    message2,
    qualifiesMessage,
    url1,
    url2,
  ) => {
    const productType = item.product_type.toLowerCase()
    const type1Lower = type1.toLowerCase().split(',')
    const type2Lower = type2.toLowerCase().split(',')

    if (!type1Lower.includes(productType) && !type2Lower.includes(productType)) {
      return
    }

    let messageToAdd
    let urlToAdd

    if (
      type1Lower.includes(productType) &&
      !lineItemsWithProductsSets.find(item =>
        type2Lower.includes(item.product_type.toLowerCase()),
      )
    ) {
      messageToAdd = message1
      urlToAdd = url2
    } else if (
      type2Lower.includes(productType) &&
      !lineItemsWithProductsSets.find(item =>
        type1Lower.includes(item.product_type.toLowerCase()),
      )
    ) {
      messageToAdd = message2
      urlToAdd = url1
    } else {
      messageToAdd = qualifiesMessage
    }

    if (!messageArray.find(mess => mess.message === messageToAdd)) {
      messageArray.push({ message: messageToAdd, url: urlToAdd })
    }
  }

  lineItemsWithProductsSets.forEach(item => {
    set1Enabled &&
      getSetMessage(
        item,
        set1Type1,
        set1Type2,
        set1Message1,
        set1Message2,
        set1QualifiesMessage,
        set1Url1,
        set1Url2,
      )
    set2Enabled &&
      getSetMessage(
        item,
        set2Type1,
        set2Type2,
        set2Message1,
        set2Message2,
        set2QualifiesMessage,
        set2Url1,
        set2Url2,
      )
    set3Enabled &&
      getSetMessage(
        item,
        set3Type1,
        set3Type2,
        set3Message1,
        set3Message2,
        set3QualifiesMessage,
        set3Url1,
        set3Url2,
      )
    if(item.line_level_total_discount > 0 && item.discounts[0].title === 'Bed and Mattress Saving') {
      discountPrice += item.line_level_total_discount;
    }
  })
  messageArray.forEach(message => {
    if(message.message === 'Saved £100 on bed and mattress') {
      message.message = `Saved ${Shopify.formatMoney(discountPrice)} on bed and mattress`
    }
  })

  if(messageArray.length > 0){
    let deliveryPromoHtml = ``;
    messageArray.map((deliveryPromo, idx) => {
      const Tag = deliveryPromo.url ? 'a' : 'p'
      deliveryPromoHtml += `<${Tag} class="psh-Body_DeliveryPromo ${deliveryPromo.url ? ' psh-Body_DeliveryPromo-link' : ''}" ${deliveryPromo.url ? `href="${deliveryPromo.url}"` : ''}>
        <svg viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="9.5" cy="9.5" r="8.5" stroke="currentColor"/><path d="M11.06 5.491c0-.408-.258-.666-.639-.666-.516 0-.83.517-.83.952 0 .422.286.653.613.653.53 0 .857-.517.857-.939Zm-.72 3.36c0-.83-.408-1.306-1.02-1.306-.789 0-1.374.789-1.945 2.026l.462.204c.436-.843.83-1.536 1.265-1.536.204 0 .259.19.259.38 0 1.088-1.17 2.83-1.184 4.108-.013.761.422 1.17 1.02 1.17.64 0 1.252-.49 1.823-2.163l-.463-.15c-.38.993-.707 1.51-1.047 1.51-.231 0-.313-.177-.313-.354 0-.897 1.143-2.76 1.143-3.89Z" fill="currentColor"/></svg>
        ${deliveryPromo.message}
      </${Tag}>`
    })
    const promoDiv = getElement('.drawer__promos');
    promoDiv.innerHTML = deliveryPromoHtml;
  }
}
